import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress } from '@backstage/core-components';
import { doraMetricsApiRef } from '../api/types';
import { niceHourTicks, niceCountTicks } from '../api/chartUtils';
import { doraMetricsTranslationRef } from '../translation';
import type {
  DoraEnvironment,
  DoraHistoryPoint,
  DoraMetrics,
  DoraMetricValue,
  DoraTargets,
  PrDetail,
} from '../api/types';
import { RatingBadge } from './ui/badge';
import { Card, CardLabel, CardFooter, CardDescription } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { ExternalLink } from 'lucide-react';
import { Alert as BuiAlert } from '@backstage/ui';
import { doraDarkTheme, doraDurationGradient, doraTheme } from './theme';

const ANNOTATION_PROJECT_SLUG = 'gitflic.ru/project-slug';
const ANNOTATION_SOURCE_LOCATION = 'backstage.io/source-location';
const ANNOTATION_VIEW_URL = 'backstage.io/view-url';
/** Override the global environments list for this specific entity. JSON array of DoraEnvironment objects. */
const ANNOTATION_ENVIRONMENTS = 'dora-metrics/environments';
/** Override the global performance targets for this specific entity. JSON object matching DoraTargets. */
const ANNOTATION_TARGETS = 'dora-metrics/targets';

function isGitflicAccessDeniedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);

  return /Gitflic API request failed:\s*403\b/.test(message);
}

/** Parse entity annotation overrides. Returns undefined for malformed JSON to fail gracefully. */
function parseAnnotationJson<T>(raw: string | undefined): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function getAnnotationUrl(raw: string | undefined): URL | undefined {
  if (!raw) return undefined;
  const value = raw.startsWith('url:') ? raw.slice('url:'.length) : raw;

  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

function getEntityWebBaseUrl(
  annotations: Record<string, string>,
): string | undefined {
  return (
    getAnnotationUrl(annotations[ANNOTATION_SOURCE_LOCATION])?.origin ??
    getAnnotationUrl(annotations[ANNOTATION_VIEW_URL])?.origin
  );
}

function getAuthorUrl(
  author: string,
  entityWebBaseUrl: string | undefined,
): string {
  const userPath = `/user/${encodeURIComponent(author)}`;
  return entityWebBaseUrl
    ? new URL(userPath, entityWebBaseUrl).toString()
    : userPath;
}

function getPrUrl(url: string, entityWebBaseUrl: string | undefined): string {
  if (!entityWebBaseUrl) {
    return url;
  }

  try {
    return new URL(url, entityWebBaseUrl).toString();
  } catch {
    return url;
  }
}

const DATE_RANGE_OPTIONS = [
  { value: '7' },
  { value: '14' },
  { value: '30' },
  { value: '60' },
  { value: '90' },
];

// ─── Helpers ───────────────────────────────────────────────────────────────

type DurationLabels = {
  dayShort: string;
  hourShort: string;
  minuteShort: string;
};

const DEFAULT_DURATION_LABELS: DurationLabels = {
  dayShort: 'd',
  hourShort: 'h',
  minuteShort: 'm',
};

function formatDuration(
  hours: number,
  labels: DurationLabels = DEFAULT_DURATION_LABELS,
): string {
  const totalMinutes = Math.round(hours * 60);
  const d = Math.floor(totalMinutes / (24 * 60));
  const h = Math.floor((totalMinutes % (24 * 60)) / 60);
  const m = totalMinutes % 60;
  if (d > 0) {
    return h > 0
      ? `${d}${labels.dayShort} ${h}${labels.hourShort}`
      : `${d}${labels.dayShort}`;
  }
  if (h > 0) {
    return m > 0
      ? `${h}${labels.hourShort} ${m}${labels.minuteShort}`
      : `${h}${labels.hourShort}`;
  }
  return `${m}${labels.minuteShort}`;
}

function formatHistoryLabels(
  points: DoraHistoryPoint[],
  locale: string,
): string[] {
  return points.map((point, index) => {
    const nextMid = points[index + 1]?.bucketMidMs;
    const prevMid = points[index - 1]?.bucketMidMs;
    const halfBucketMs =
      typeof nextMid === 'number'
        ? (nextMid - point.bucketMidMs) / 2
        : typeof prevMid === 'number'
        ? (point.bucketMidMs - prevMid) / 2
        : 0;
    const labelDate = new Date(
      Math.min(point.bucketMidMs + halfBucketMs, Date.now()),
    );

    return labelDate.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    });
  });
}

function durationColor(index: number, total: number): string {
  if (total <= 1) return doraDurationGradient[0];
  const step = (doraDurationGradient.length - 1) / (total - 1);
  return doraDurationGradient[Math.round(index * step)];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Period-over-period delta — compares last bucket vs second-to-last bucket
function buildDeltaLabel(
  values: number[],
  unit: string,
  lowerIsBetter: boolean,
  labels: {
    duration: DurationLabels;
    improving: string;
    worsening: string;
    perWeekShort: string;
    tooltip: (current: string, previous: string) => string;
  },
): { text: string; tooltip: string; good: boolean } | null {
  if (values.length < 2) return null;
  const curr = values[values.length - 1];
  const prev = values[values.length - 2];
  const diff = curr - prev;
  if (Math.abs(diff) < 0.001) return null;
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  const arrow = diff > 0 ? '↑' : '↓';

  const fmt = (v: number) =>
    unit === 'hours'
      ? formatDuration(v, labels.duration)
      : unit === '%'
      ? `${Math.round(v * 10) / 10}%`
      : unit === 'per week'
      ? `${Math.round(v * 10) / 10}/${labels.perWeekShort}`
      : `${Math.round(v * 10) / 10} ${unit}`;

  const direction = good ? labels.improving : labels.worsening;
  return {
    text: `${arrow} ${fmt(Math.abs(diff))} ${direction}`,
    tooltip: labels.tooltip(fmt(curr), fmt(prev)),
    good,
  };
}

// ─── Compact sparkline (with hover tooltip + date axis) ────────────────────

function CompactSparkline({
  values,
  weekLabels,
  color,
  type,
  formatValue,
  height = 36,
}: {
  values: number[];
  weekLabels: string[];
  color: string;
  type: 'bar' | 'line';
  formatValue: (v: number) => string;
  height?: number;
}) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const VW = 400; // viewBox units
  const mutedBorder = isDark
    ? doraTheme.chart.darkMutedBorder
    : doraTheme.chart.lightMutedBorder;
  const tooltipBg = isDark
    ? doraDarkTheme.appBackground
    : doraTheme.light.surface;
  const tooltipBdr = isDark
    ? doraDarkTheme.strongBorder
    : doraTheme.light.border;
  const tooltipText = isDark
    ? doraDarkTheme.primaryText
    : doraTheme.light.primaryText;
  const axisLabelColor = isDark
    ? doraDarkTheme.primaryText
    : doraTheme.light.mutedText;
  const font = 'Inter,system-ui,sans-serif';

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    setHoverIdx(
      Math.max(
        0,
        Math.min(values.length - 1, Math.round(relX * (values.length - 1))),
      ),
    );
  };

  if (type === 'bar') {
    const max = Math.max(...values, 1);
    const gap = 4;
    const barW = (VW - gap * (values.length - 1)) / values.length;

    const hoverX =
      hoverIdx !== null ? hoverIdx * (barW + gap) + barW / 2 : null;
    const tooltipX = hoverX !== null ? Math.min(hoverX - 34, VW - 72) : 0;

    return (
      <div>
        <svg
          viewBox={`0 0 ${VW} ${height}`}
          width="100%"
          style={{ display: 'block', overflow: 'visible' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {values.map((v, i) => {
            const h = Math.max(2, (v / max) * (height - 4));
            return (
              <rect
                key={i}
                x={i * (barW + gap)}
                y={height - h}
                width={barW}
                height={h}
                rx={1.5}
                fill={color}
                opacity={i === hoverIdx ? 1 : 0.3 + (i / values.length) * 0.55}
              />
            );
          })}
          <line
            x1={0}
            y1={height - 0.5}
            x2={VW}
            y2={height - 0.5}
            stroke={mutedBorder}
            strokeWidth={1}
          />
          {hoverIdx !== null && hoverX !== null && (
            <>
              <rect
                x={tooltipX}
                y={2}
                width={72}
                height={22}
                rx={4}
                fill={tooltipBg}
                stroke={tooltipBdr}
                strokeWidth={1}
              />
              <text
                x={tooltipX + 36}
                y={17}
                textAnchor="middle"
                fill={tooltipText}
                fontSize={9}
                fontWeight={600}
                fontFamily={font}
              >
                {weekLabels[hoverIdx]}: {formatValue(values[hoverIdx])}
              </text>
            </>
          )}
        </svg>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 3,
          }}
        >
          <span style={{ fontSize: 9, color: axisLabelColor }}>
            {weekLabels[0]}
          </span>
          <span style={{ fontSize: 9, color: axisLabelColor }}>
            {weekLabels[weekLabels.length - 1]}
          </span>
        </div>
      </div>
    );
  }

  if (values.length < 2) return null;
  const max = Math.max(...values, 0.01);
  const min = Math.min(...values);
  const range = max - min || max || 1;
  const pad = 3;
  const pts = values.map((v, i): [number, number] => [
    (i / (values.length - 1)) * (VW - pad * 2) + pad,
    height - pad * 2 - ((v - min) / range) * (height - pad * 2 - 2) + pad,
  ]);
  const d = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const area = `${d} L${pts[pts.length - 1][0]},${height} L${
    pts[0][0]
  },${height} Z`;
  const hoverPt = hoverIdx !== null ? pts[hoverIdx] : null;
  const tooltipX2 = hoverPt ? Math.min(hoverPt[0] - 34, VW - 72) : 0;

  return (
    <div>
      <svg
        viewBox={`0 0 ${VW} ${height}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <path d={area} fill={color} opacity={0.07} />
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {pts.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={i === hoverIdx ? 3.5 : i === pts.length - 1 ? 2.5 : 0}
            fill={color}
          />
        ))}
        <line
          x1={0}
          y1={height - 0.5}
          x2={VW}
          y2={height - 0.5}
          stroke={mutedBorder}
          strokeWidth={1}
        />
        {hoverPt && hoverIdx !== null && (
          <>
            <line
              x1={hoverPt[0]}
              y1={0}
              x2={hoverPt[0]}
              y2={height}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="2 2"
              opacity={0.4}
            />
            <rect
              x={tooltipX2}
              y={2}
              width={72}
              height={22}
              rx={4}
              fill={tooltipBg}
              stroke={tooltipBdr}
              strokeWidth={1}
            />
            <text
              x={tooltipX2 + 36}
              y={17}
              textAnchor="middle"
              fill={tooltipText}
              fontSize={9}
              fontWeight={600}
              fontFamily={font}
            >
              {weekLabels[hoverIdx]}: {formatValue(values[hoverIdx])}
            </text>
          </>
        )}
      </svg>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 3,
        }}
      >
        <span style={{ fontSize: 9, color: axisLabelColor }}>
          {weekLabels[0]}
        </span>
        <span style={{ fontSize: 9, color: axisLabelColor }}>
          {weekLabels[weekLabels.length - 1]}
        </span>
      </div>
    </div>
  );
}

// ─── Detailed interactive chart ────────────────────────────────────────────

interface ChartTooltipState {
  x: number;
  y: number;
  primary: string;
  secondary?: string;
}

function DetailedChart({
  values,
  weekLabels,
  bucketMidMs,
  color,
  type,
  formatValue,
  tickUnit,
  prs,
  days,
  dateLocale,
  hoveredPrNumber,
  onPrHover,
}: {
  values: number[];
  weekLabels: string[];
  bucketMidMs: number[];
  color: string;
  type: 'bar' | 'line';
  formatValue: (v: number) => string;
  tickUnit?: 'hours' | 'count';
  prs?: PrDetail[];
  days: number;
  dateLocale: string;
  hoveredPrNumber?: number | null;
  onPrHover?: (num: number | null) => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const W = 800,
    H = 260;
  const PAD = { top: 16, right: 16, bottom: 36, left: 56 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  // Y scale: nice tick-aligned range so axis labels are clean intervals.
  const prMaxDuration =
    prs && prs.length > 0 ? Math.max(...prs.map(p => p.durationHours)) : 0;
  const rawDataMax = Math.max(
    ...values.filter(v => v > 0),
    prMaxDuration,
    0.01,
  );
  const yTicks =
    tickUnit === 'hours'
      ? niceHourTicks(rawDataMax)
      : tickUnit === 'count'
      ? niceCountTicks(rawDataMax)
      : [0, 0.25, 0.5, 0.75, 1].map(t => t * rawDataMax);
  const dataMin = 0;
  const dataMax = yTicks[yTicks.length - 1];
  const dataRange = dataMax - dataMin || 1;
  const yScale = (v: number) => cH - ((v - dataMin) / dataRange) * cH;

  // Shared timestamp-based x-axis
  const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
  const rangeMs = days * 24 * 60 * 60 * 1000;
  const xTime = (ms: number) =>
    Math.max(0, Math.min(cW, ((ms - cutoffMs) / rangeMs) * cW));

  const xIdx = (i: number) =>
    bucketMidMs.length === values.length
      ? xTime(bucketMidMs[i])
      : values.length > 1
      ? (i / (values.length - 1)) * cW
      : cW / 2;

  const pts: [number, number][] = values.map((v, i) => [
    PAD.left + xIdx(i),
    PAD.top + yScale(v),
  ]);
  const linePath = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const areaPath =
    pts.length > 1
      ? `${linePath} L${pts[pts.length - 1][0]},${PAD.top + cH} L${pts[0][0]},${
          PAD.top + cH
        } Z`
      : '';

  const gap = 6;
  const barW =
    values.length > 1 ? (cW - gap * (values.length - 1)) / values.length : cW;

  const gridColor = isDark
    ? doraTheme.chart.darkGrid
    : doraTheme.chart.lightGrid;
  const axisColor = isDark
    ? doraDarkTheme.strongBorder
    : doraTheme.light.strongBorder;
  const labelColor = isDark
    ? doraDarkTheme.primaryText
    : doraTheme.light.mutedText;
  const font = 'Inter,system-ui,sans-serif';
  const gradId = `dg_${color.replace('#', '')}`;

  // Find the nearest bucket index for a given timestamp
  const nearestBucket = (ms: number): number => {
    if (bucketMidMs.length === 0) return 0;
    let best = 0,
      bestDist = Infinity;
    bucketMidMs.forEach((mid, i) => {
      const d = Math.abs(ms - mid);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX =
      (e.clientX - rect.left - PAD.left * (rect.width / W)) /
      ((rect.width * cW) / W);
    const idx = Math.max(
      0,
      Math.min(values.length - 1, Math.round(relX * (values.length - 1))),
    );
    setHoverIdx(idx);
    const cRect = containerRef.current?.getBoundingClientRect();
    const x = cRect ? e.clientX - cRect.left : 0;
    const y = cRect ? e.clientY - cRect.top : 0;
    setTooltip({
      x,
      y,
      primary: formatValue(values[idx]),
      secondary: weekLabels[idx],
    });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block' }}
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={() => {
          setHoverIdx(null);
          setTooltip(null);
        }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
          {/* Clip MR dots to the chart area */}
          <clipPath id={`prClip_${gradId}`}>
            <rect x={PAD.left} y={PAD.top - 8} width={cW} height={cH + 16} />
          </clipPath>
        </defs>

        {/* Grid + Y-axis */}
        {yTicks.map((v, i) => {
          const y = PAD.top + yScale(v);
          return (
            <g key={i}>
              {i > 0 && (
                <line
                  x1={PAD.left}
                  y1={y}
                  x2={PAD.left + cW}
                  y2={y}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="3 4"
                />
              )}
              <text
                x={PAD.left - 6}
                y={y + 4}
                textAnchor="end"
                fill={labelColor}
                fontSize={10}
                fontFamily={font}
              >
                {formatValue(v)}
              </text>
            </g>
          );
        })}
        <line
          x1={PAD.left}
          y1={PAD.top + cH}
          x2={PAD.left + cW}
          y2={PAD.top + cH}
          stroke={axisColor}
          strokeWidth={1}
        />

        {/* Chart body */}
        {type === 'bar' ? (
          values.map((v, i) => {
            const bH = Math.max(2, ((v - dataMin) / dataRange) * cH);
            const x = PAD.left + i * (barW + gap);
            return (
              <rect
                key={i}
                x={x}
                y={PAD.top + cH - bH}
                width={barW}
                height={bH}
                rx={3}
                fill={color}
                opacity={
                  i === hoverIdx
                    ? 0.95
                    : 0.4 + (i / Math.max(values.length - 1, 1)) * 0.45
                }
              />
            );
          })
        ) : (
          <>
            {/* When prs are provided, show scatter-only (no line/area/bucket dots).
                For CFR (no prs), keep a minimal line + non-zero bucket dots. */}
            {!prs && areaPath && <path d={areaPath} fill={`url(#${gradId})`} />}
            {!prs && (
              <path
                d={linePath}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {!prs &&
              pts.map(
                ([x, y], i) =>
                  values[i] > 0 && (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r={i === hoverIdx ? 5.5 : 4}
                      fill={
                        isDark ? doraDarkTheme.surface : doraTheme.light.surface
                      }
                      stroke={color}
                      strokeWidth={i === hoverIdx ? 2.5 : 1.5}
                    />
                  ),
              )}
          </>
        )}

        {/* MR data-point dots — x at bucket midpoint, y at actual duration (clamped to chart) */}
        {prs && type === 'line' && (
          <g clipPath={`url(#prClip_${gradId})`}>
            {prs.map((pr, dotIdx) => {
              const mergedMs = new Date(pr.mergedAt).getTime();
              const bIdx = nearestBucket(mergedMs);
              // Small deterministic jitter within the bucket to avoid overlap
              const jitter = (((pr.number * 17 + dotIdx * 11) % 13) - 6) * 5;
              const px = PAD.left + xIdx(bIdx) + jitter;
              // Clamp y: MR may be faster or slower than bucket avg; always visible
              const rawPy = PAD.top + yScale(pr.durationHours);
              const py = Math.max(
                PAD.top + 4,
                Math.min(PAD.top + cH - 4, rawPy),
              );
              const isHov = pr.number === hoveredPrNumber;

              return (
                <g
                  key={pr.number}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => {
                    e.stopPropagation();
                    const cRect = containerRef.current?.getBoundingClientRect();
                    const x = cRect ? e.clientX - cRect.left : 0;
                    const y = cRect ? e.clientY - cRect.top : 0;
                    setTooltip({
                      x,
                      y,
                      primary: `#${pr.number}: ${pr.title}`,
                      secondary: `${formatValue(pr.durationHours)} · ${new Date(
                        pr.mergedAt,
                      ).toLocaleDateString(dateLocale, {
                        month: 'short',
                        day: 'numeric',
                      })}`,
                    });
                    onPrHover?.(pr.number);
                  }}
                  onMouseLeave={() => {
                    setTooltip(null);
                    onPrHover?.(null);
                  }}
                >
                  {/* connector removed — no bucket-average dot to connect to */}
                  <circle
                    cx={px}
                    cy={py}
                    r={isHov ? 11 : 7}
                    fill={color}
                    opacity={isHov ? 0.2 : 0.1}
                  />
                  <circle
                    cx={px}
                    cy={py}
                    r={isHov ? 5.5 : 3.5}
                    fill={
                      isHov
                        ? color
                        : isDark
                        ? doraDarkTheme.surface
                        : doraTheme.light.surface
                    }
                    stroke={color}
                    strokeWidth={isHov ? 0 : 1.5}
                    opacity={isHov ? 1 : 0.8}
                  />
                </g>
              );
            })}
          </g>
        )}

        {/* Hover crosshair */}
        {hoverIdx !== null && type === 'line' && pts[hoverIdx] && (
          <>
            <line
              x1={pts[hoverIdx][0]}
              y1={PAD.top}
              x2={pts[hoverIdx][0]}
              y2={PAD.top + cH}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.3}
            />
            <line
              x1={PAD.left}
              y1={pts[hoverIdx][1]}
              x2={PAD.left + cW}
              y2={pts[hoverIdx][1]}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.2}
            />
          </>
        )}

        {/* X-axis labels */}
        {weekLabels.map((lbl, i) => {
          const total = weekLabels.length;
          const step = Math.max(1, Math.floor(total / 5));
          if (i !== 0 && i !== total - 1 && i % step !== 0) return null;
          const x =
            type === 'bar'
              ? PAD.left + i * (barW + gap) + barW / 2
              : PAD.left + xIdx(i);
          return (
            <text
              key={i}
              x={x}
              y={H - 8}
              textAnchor="middle"
              fill={labelColor}
              fontSize={10}
              fontFamily={font}
            >
              {lbl}
            </text>
          );
        })}
      </svg>

      {/* Absolute tooltip — positioned relative to the chart container */}
      {tooltip &&
        (() => {
          const TW = 220;
          const cW2 = containerRef.current?.offsetWidth ?? 800;
          const nearRight = tooltip.x + TW + 12 > cW2;
          return (
            <div
              style={{
                position: 'absolute',
                left: nearRight ? tooltip.x - TW - 8 : tooltip.x + 8,
                top: tooltip.y - 36,
                zIndex: 9999,
                pointerEvents: 'none',
                background: isDark
                  ? doraDarkTheme.appBackground
                  : doraTheme.light.surface,
                border: `1px solid ${
                  isDark ? doraDarkTheme.strongBorder : doraTheme.light.border
                }`,
                borderRadius: 7,
                padding: '7px 10px',
                boxShadow: isDark
                  ? doraTheme.shadow.darkTooltip
                  : doraTheme.shadow.lightTooltip,
                width: TW,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isDark
                    ? doraDarkTheme.primaryText
                    : doraTheme.light.primaryText,
                  fontFamily: font,
                  lineHeight: 1.4,
                }}
              >
                {tooltip.primary}
              </div>
              {tooltip.secondary && (
                <div
                  style={{
                    fontSize: 11,
                    color: isDark
                      ? doraDarkTheme.primaryText
                      : doraTheme.light.mutedText,
                    fontFamily: font,
                    marginTop: 2,
                  }}
                >
                  {tooltip.secondary}
                </div>
              )}
            </div>
          );
        })()}
    </div>
  );
}

// ─── Detailed overlay ──────────────────────────────────────────────────────

interface ExpandedCardData {
  title: string;
  metric: DoraMetricValue;
  description: string;
  lowerIsBetter: boolean;
  sparklineValues: number[];
  sparklineType: 'bar' | 'line';
  sparklineColor: string;
  tickUnit?: 'hours' | 'count';
  weekLabels: string[];
  bucketMidMs: number[];
  prs?: PrDetail[];
  /** Override the section header above the MR list (default: "All deployments"). */
  prListLabel?: string;
}

function DetailedOverlay({
  data,
  days,
  entityWebBaseUrl,
  onClose,
}: {
  data: ExpandedCardData;
  days: number;
  entityWebBaseUrl?: string;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslationRef(doraMetricsTranslationRef);
  const isDark = theme.palette.type === 'dark';
  const [hoveredPrNumber, setHoveredPrNumber] = useState<number | null>(null);
  const prRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const durationLabels: DurationLabels = {
    dayShort: t('duration.dayShort'),
    hourShort: t('duration.hourShort'),
    minuteShort: t('duration.minuteShort'),
  };
  const dateLocale = t('dates.locale');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Auto-scroll to the highlighted MR row when hover comes from the chart
  useEffect(() => {
    if (hoveredPrNumber !== null) {
      const el = prRowRefs.current.get(hoveredPrNumber);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hoveredPrNumber]);

  const {
    title,
    metric,
    description,
    lowerIsBetter,
    sparklineValues,
    sparklineType,
    sparklineColor,
    tickUnit,
    weekLabels,
    bucketMidMs,
    prs,
    prListLabel,
  } = data;
  const isDeployFreq = sparklineType === 'bar';
  const displayValue =
    metric.unit === 'hours'
      ? formatDuration(metric.value, durationLabels)
      : String(metric.value);
  const displayUnit =
    metric.unit === 'hours'
      ? ''
      : metric.unit === 'per week'
      ? `/${t('delta.perWeekShort')}`
      : metric.unit;
  const formatVal = (v: number) =>
    metric.unit === 'hours'
      ? formatDuration(v, durationLabels)
      : `${Math.round(v * 10) / 10}`;
  const borderColor = isDark ? doraDarkTheme.border : doraTheme.light.border;
  const mutedColor = isDark
    ? doraDarkTheme.primaryText
    : doraTheme.light.mutedText;
  const targetLabel = lowerIsBetter
    ? t('metric.targetLower', {
        value:
          metric.unit === 'hours'
            ? formatDuration(metric.target, durationLabels)
            : String(metric.target),
        unit: displayUnit,
      }).trim()
    : t('metric.targetHigher', {
        value: String(metric.target),
        unit: displayUnit,
      }).trim();

  // Group deployment MRs by date (for the date table)
  const prsByDate =
    isDeployFreq && prs
      ? prs.reduce((acc, pr) => {
          const date = new Date(pr.mergedAt).toLocaleDateString(dateLocale, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          if (!acc[date]) acc[date] = [];
          acc[date].push(pr);
          return acc;
        }, {} as Record<string, PrDetail[]>)
      : {};
  const sortedDates = Object.keys(prsByDate).sort(
    (a, b) =>
      new Date(prsByDate[b][0].mergedAt).getTime() -
      new Date(prsByDate[a][0].mergedAt).getTime(),
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 499,
          background: isDark
            ? doraTheme.overlay.darkBackdrop
            : doraTheme.overlay.lightBackdrop,
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
          width: 'min(92vw, 960px)',
          maxHeight: 'calc(100vh - 80px)',
          zIndex: 500,
          background: isDark
            ? doraDarkTheme.surface
            : doraTheme.light.appBackground,
          border: `1px solid ${borderColor}`,
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'detailExpandIn 0.2s cubic-bezier(0.34,1.2,0.64,1)',
          boxShadow: isDark
            ? doraTheme.shadow.darkOverlay
            : doraTheme.shadow.lightOverlay,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '20px 24px 16px',
            borderBottom: `1px solid ${borderColor}`,
            flexShrink: 0,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: mutedColor,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                marginBottom: 8,
              }}
            >
              {title} · {weekLabels[0]} – {weekLabels[weekLabels.length - 1]}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 52,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                {displayValue}
              </span>
              {displayUnit && (
                <span
                  style={{ fontSize: 18, color: mutedColor, fontWeight: 500 }}
                >
                  {displayUnit}
                </span>
              )}
              <RatingBadge rating={metric.rating} />
            </div>
            <div style={{ fontSize: 12, color: mutedColor, marginTop: 6 }}>
              {description} ·{' '}
              <span style={{ fontStyle: 'italic' }}>{targetLabel}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              all: 'unset',
              cursor: 'pointer',
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: isDark
                ? doraTheme.overlay.darkControl
                : doraTheme.overlay.lightControl,
              color: mutedColor,
              fontSize: 18,
              fontWeight: 300,
              transition: 'all 0.12s',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = isDark
                ? doraTheme.overlay.darkControlHover
                : doraTheme.overlay.lightControlHover;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = isDark
                ? doraTheme.overlay.darkControl
                : doraTheme.overlay.lightControl;
            }}
          >
            ✕
          </button>
        </div>

        {/* Body — scrollable */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* Chart */}
          {weekLabels.length >= 2 ? (
            <DetailedChart
              values={sparklineValues}
              weekLabels={weekLabels}
              bucketMidMs={bucketMidMs}
              color={sparklineColor}
              type={sparklineType}
              tickUnit={tickUnit}
              formatValue={formatVal}
              prs={isDeployFreq ? undefined : prs}
              days={days}
              dateLocale={dateLocale}
              hoveredPrNumber={hoveredPrNumber}
              onPrHover={setHoveredPrNumber}
            />
          ) : (
            <div
              style={{
                color: mutedColor,
                fontSize: 13,
                textAlign: 'center',
                padding: '48px 0',
              }}
            >
              {t('details.notEnoughData')}
            </div>
          )}

          {/* === Deployment Frequency: MRs grouped by date === */}
          {isDeployFreq && prs && prs.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: mutedColor,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  marginBottom: 12,
                  borderTop: `1px solid ${borderColor}`,
                  paddingTop: 12,
                }}
              >
                {prListLabel ?? t('details.allDeployments')} ·{' '}
                {t('details.prCount', {
                  value: String(prs.length),
                  plural: prs.length !== 1 ? 's' : '',
                })}
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                {sortedDates.map(date => (
                  <div key={date}>
                    {/* Date header */}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: sparklineColor,
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <span>{date}</span>
                      <span style={{ color: mutedColor, fontWeight: 500 }}>
                        ·{' '}
                        {t('details.prCount', {
                          value: String(prsByDate[date].length),
                          plural: prsByDate[date].length > 1 ? 's' : '',
                        })}
                      </span>
                    </div>
                    {/* MR rows */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        paddingLeft: 12,
                        borderLeft: `2px solid ${sparklineColor}22`,
                      }}
                    >
                      {prsByDate[date].map(pr => (
                        <div
                          key={pr.number}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 5,
                            padding: '7px 10px',
                            borderRadius: 7,
                            background: isDark
                              ? doraDarkTheme.appBackground
                              : doraTheme.light.raisedSurface,
                            border: `1px solid ${borderColor}`,
                            transition: 'background 0.12s',
                          }}
                        >
                          {/* Row 1: number + title + lead time + link */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: mutedColor,
                                flexShrink: 0,
                                minWidth: 36,
                              }}
                            >
                              #{pr.number}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                color: theme.palette.text.primary,
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={pr.title}
                            >
                              {pr.title}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                color: mutedColor,
                                flexShrink: 0,
                              }}
                            >
                              {t('details.leadSuffix', {
                                duration: formatDuration(
                                  pr.durationHours,
                                  durationLabels,
                                ),
                              })}
                            </span>
                            <a
                              href={getPrUrl(pr.url, entityWebBaseUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: mutedColor, flexShrink: 0 }}
                            >
                              <ExternalLink size={11} />
                            </a>
                          </div>
                          {/* Row 2: author badge */}
                          {pr.author && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                              }}
                            >
                              {pr.authorAvatar ? (
                                <img
                                  src={pr.authorAvatar}
                                  alt={pr.author}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    border: `1px solid ${
                                      isDark
                                        ? doraDarkTheme.strongBorder
                                        : doraTheme.light.border
                                    }`,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    background: isDark
                                      ? doraDarkTheme.raisedSurface
                                      : doraTheme.light.border,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 8,
                                    fontWeight: 700,
                                    color: mutedColor,
                                  }}
                                >
                                  {pr.author[0].toUpperCase()}
                                </div>
                              )}
                              <a
                                href={getAuthorUrl(pr.author, entityWebBaseUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: 10,
                                  fontWeight: 500,
                                  color: mutedColor,
                                  textDecoration: 'none',
                                  letterSpacing: '0.01em',
                                }}
                                onMouseEnter={e => {
                                  (e.currentTarget as HTMLElement).style.color =
                                    doraTheme.dora.orange;
                                }}
                                onMouseLeave={e => {
                                  (e.currentTarget as HTMLElement).style.color =
                                    mutedColor;
                                }}
                              >
                                @{pr.author}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === Lead Time / MTTR: duration-ranked MR list with hover linking === */}
          {!isDeployFreq && prs && prs.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: mutedColor,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  marginBottom: 10,
                  borderTop: `1px solid ${borderColor}`,
                  paddingTop: 12,
                }}
              >
                {title === t('metric.meanTimeToRestore.title')
                  ? t('details.slowestHotfixPrs')
                  : t('details.slowestPrs')}{' '}
                · {t('details.hoverRowToHighlight')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {prs.map((pr, i) => {
                  const col = durationColor(i, prs.length);
                  const maxDur = prs[0].durationHours;
                  const barPct =
                    maxDur > 0 ? (pr.durationHours / maxDur) * 100 : 0;
                  const isHov = pr.number === hoveredPrNumber;
                  return (
                    <div
                      key={pr.number}
                      ref={el => {
                        if (el) prRowRefs.current.set(pr.number, el);
                        else prRowRefs.current.delete(pr.number);
                      }}
                      onMouseEnter={() => setHoveredPrNumber(pr.number)}
                      onMouseLeave={() => setHoveredPrNumber(null)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: isHov
                          ? isDark
                            ? doraDarkTheme.raisedSurface
                            : doraTheme.light.selectedSurface
                          : isDark
                          ? doraDarkTheme.appBackground
                          : doraTheme.light.hoverSurface,
                        border: `1px solid ${
                          isHov ? `${sparklineColor}55` : borderColor
                        }`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                        transition: 'background 0.12s, border-color 0.12s',
                        cursor: 'default',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: col,
                            flexShrink: 0,
                          }}
                        >
                          #{pr.number}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            color: theme.palette.text.primary,
                            flex: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={pr.title}
                        >
                          {pr.title}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: mutedColor,
                            flexShrink: 0,
                          }}
                        >
                          {new Date(pr.mergedAt).toLocaleDateString(
                            dateLocale,
                            {
                              month: 'short',
                              day: 'numeric',
                            },
                          )}
                        </span>
                        <a
                          href={getPrUrl(pr.url, entityWebBaseUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: mutedColor,
                            flexShrink: 0,
                            transition: 'color 0.12s',
                          }}
                        >
                          <ExternalLink size={11} />
                        </a>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <AnimatedBar widthPct={barPct} color={col} />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: col,
                            minWidth: 52,
                            textAlign: 'right',
                            flexShrink: 0,
                          }}
                        >
                          {formatDuration(pr.durationHours, durationLabels)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes detailExpandIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-46%); }
          to   { opacity: 1; transform: translateX(-50%) translateY(-50%); }
        }
      `}</style>
    </>
  );
}

// ─── Animated bar ──────────────────────────────────────────────────────────

function AnimatedBar({ widthPct, color }: { widthPct: number; color: string }) {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  useEffect(() => {
    const timer = setTimeout(() => setWidth(widthPct), 80);
    return () => clearTimeout(timer);
  }, [widthPct]);

  return (
    <div
      ref={ref}
      style={{
        background: isDark
          ? doraDarkTheme.raisedSurface
          : doraTheme.light.progressTrack,
        borderRadius: 4,
        height: 5,
        overflow: 'hidden',
        flex: 1,
        minWidth: 40,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${width}%`,
          background: color,
          borderRadius: 4,
          transition: 'width 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: `0 0 6px ${color}88`,
        }}
      />
    </div>
  );
}

// ─── Metric cards ──────────────────────────────────────────────────────────

function MetricTitle({ title, tooltip }: { title: string; tooltip: string }) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  return (
    <CardLabel>
      {title}
      {'\u2060'}
      <Tooltip title={tooltip} enterDelay={100} leaveDelay={0} arrow>
        <span
          aria-label={tooltip}
          onClick={event => event.stopPropagation()}
          style={{
            display: 'inline',
            padding: '0 4px',
            borderRadius: '50%',
            border: `1px solid ${
              isDark ? doraDarkTheme.strongBorder : doraTheme.light.hoverBorder
            }`,
            color: isDark
              ? doraDarkTheme.primaryText
              : doraTheme.light.strongBorder,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '14px',
            cursor: 'help',
            verticalAlign: 'baseline',
            whiteSpace: 'nowrap',
            textTransform: 'none',
          }}
        >
          ?
        </span>
      </Tooltip>
    </CardLabel>
  );
}

function MetricCard({
  title,
  tooltip,
  metric,
  description,
  lowerIsBetter = false,
  sparklineData,
  sparklineType = 'line',
  sparklineColor,
  weekLabels,
  onExpand,
  expandLabel,
}: {
  title: string;
  tooltip: string;
  metric: DoraMetricValue;
  description: string;
  lowerIsBetter?: boolean;
  sparklineData?: number[];
  sparklineType?: 'bar' | 'line';
  sparklineColor?: string;
  weekLabels?: string[];
  onExpand?: () => void;
  /** Persistent label shown in the card footer (instead of the hover-only "expand ↗"). */
  expandLabel?: string;
}) {
  const theme = useTheme();
  const { t } = useTranslationRef(doraMetricsTranslationRef);
  const isDark = theme.palette.type === 'dark';
  const [hovered, setHovered] = useState(false);
  const durationLabels: DurationLabels = {
    dayShort: t('duration.dayShort'),
    hourShort: t('duration.hourShort'),
    minuteShort: t('duration.minuteShort'),
  };

  const displayValue =
    metric.unit === 'hours'
      ? formatDuration(metric.value, durationLabels)
      : metric.value;
  const displayUnit =
    metric.unit === 'hours'
      ? ''
      : metric.unit === 'per week'
      ? `/${t('delta.perWeekShort')}`
      : metric.unit;
  const targetValue =
    metric.unit === 'hours'
      ? formatDuration(metric.target, durationLabels)
      : metric.target;
  const targetLabel = lowerIsBetter
    ? t('metric.targetLower', {
        value: String(targetValue),
        unit: displayUnit,
      }).trim()
    : t('metric.targetHigher', {
        value: String(targetValue),
        unit: displayUnit,
      }).trim();

  const hasSparkline =
    sparklineData &&
    sparklineData.length >= 2 &&
    weekLabels &&
    weekLabels.length >= 2;
  const delta = hasSparkline
    ? buildDeltaLabel(sparklineData!, metric.unit, lowerIsBetter, {
        duration: durationLabels,
        improving: t('delta.improving'),
        worsening: t('delta.worsening'),
        perWeekShort: t('delta.perWeekShort'),
        tooltip: (current, previous) =>
          t('delta.tooltip', { current, previous }),
      })
    : null;
  const formatVal = (v: number) =>
    metric.unit === 'hours'
      ? formatDuration(v, durationLabels)
      : `${Math.round(v * 10) / 10}`;

  return (
    <Card
      style={{ cursor: onExpand ? 'pointer' : undefined }}
      onClick={onExpand}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <MetricTitle title={title} tooltip={tooltip} />
        {onExpand && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: isDark
                ? doraDarkTheme.primaryText
                : doraTheme.light.strongBorder,
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.15s',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            {t('metric.expand')}
          </span>
        )}
      </div>
      {/* Right-aligned column: delta trend + direction hint stacked */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
          minHeight: 32,
        }}
      >
        {delta && (
          <span
            title={delta.tooltip}
            style={{
              fontSize: 10,
              fontWeight: 600,
              cursor: 'help',
              color: delta.good
                ? doraTheme.metric.good
                : doraTheme.metric.failure,
              background: delta.good
                ? isDark
                  ? doraTheme.metric.goodDeltaDark
                  : doraTheme.metric.goodDeltaLight
                : isDark
                ? doraTheme.metric.badDeltaDark
                : doraTheme.metric.badDeltaLight,
              padding: '2px 8px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
            }}
          >
            {delta.text}
          </span>
        )}
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: isDark
              ? doraDarkTheme.primaryText
              : doraTheme.light.strongBorder,
            whiteSpace: 'nowrap',
          }}
        >
          {lowerIsBetter ? t('metric.lowerBetter') : t('metric.higherBetter')}
        </span>
      </div>
      {/* Value row: big number + unit */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: isDark
              ? doraDarkTheme.primaryText
              : doraTheme.light.primaryText,
            lineHeight: 1,
          }}
        >
          {displayValue}
        </span>
        {displayUnit && (
          <span
            style={{
              fontSize: 14,
              color: isDark
                ? doraDarkTheme.primaryText
                : doraTheme.light.secondaryText,
              fontWeight: 500,
            }}
          >
            {displayUnit}
          </span>
        )}
      </div>
      <RatingBadge rating={metric.rating} />
      {hasSparkline && (
        <div style={{ marginTop: 2 }}>
          <CompactSparkline
            values={sparklineData!}
            weekLabels={weekLabels!}
            color={sparklineColor ?? doraTheme.metric.deployment}
            type={sparklineType}
            formatValue={formatVal}
          />
        </div>
      )}
      <CardDescription>{description}</CardDescription>
      <CardFooter>
        <span
          style={{
            fontWeight: 600,
            color: isDark
              ? doraDarkTheme.primaryText
              : doraTheme.light.subtleText,
          }}
        >
          {targetLabel}
        </span>
        {expandLabel && onExpand && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: doraTheme.dora.orange,
              letterSpacing: '0.04em',
              marginLeft: 'auto',
            }}
          >
            {expandLabel}
          </span>
        )}
      </CardFooter>
    </Card>
  );
}

function NaCard({
  title,
  tooltip,
  description,
}: {
  title: string;
  tooltip: string;
  description: string;
}) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  return (
    <Card>
      <MetricTitle title={title} tooltip={tooltip} />
      <div style={{ minHeight: 32 }} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: isDark
              ? doraDarkTheme.primaryText
              : doraTheme.light.subtleText,
            lineHeight: 1,
          }}
        >
          N/A
        </span>
      </div>
      <CardDescription muted>{description}</CardDescription>
    </Card>
  );
}

// ─── Filter label ──────────────────────────────────────────────────────────

function FilterLabel({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {children}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export function DoraMetricsContent() {
  const { entity } = useEntity();
  const doraApi = useApi(doraMetricsApiRef);
  const { t } = useTranslationRef(doraMetricsTranslationRef);
  const muiTheme = useTheme();
  const textPrimary = muiTheme.palette.text.primary;
  const textSecondary = muiTheme.palette.text.secondary;

  const projectSlug =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_SLUG] ?? '';
  const annotations = entity.metadata.annotations ?? {};
  const entityWebBaseUrl = getEntityWebBaseUrl(annotations);

  // Per-repo overrides from catalog-info.yaml annotations
  const annotationEnvs = parseAnnotationJson<DoraEnvironment[]>(
    annotations[ANNOTATION_ENVIRONMENTS],
  );
  const annotationTargets = parseAnnotationJson<Partial<DoraTargets>>(
    annotations[ANNOTATION_TARGETS],
  );

  const environments: DoraEnvironment[] =
    annotationEnvs ?? doraApi.getEnvironments();
  const defaultEnv = environments[0];
  const defaultDays = doraApi.getDefaultDays();

  // Use environment name as the selection key (branch can be a multi-value pattern)
  const [selectedEnvName, setSelectedEnvName] = useState<string>(
    defaultEnv?.name ?? '',
  );
  const [selectedDays, setSelectedDays] = useState<string>(String(defaultDays));

  const selectedEnv =
    environments.find(e => e.name === selectedEnvName) ?? defaultEnv;
  const days = parseInt(selectedDays, 10);

  const [metrics, setMetrics] = useState<DoraMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [history, setHistory] = useState<DoraHistoryPoint[] | null>(null);
  const [expanded, setExpanded] = useState<ExpandedCardData | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!projectSlug) {
      setError(
        new Error(
          t('errors.missingAnnotation', {
            annotation: ANNOTATION_PROJECT_SLUG,
          }),
        ),
      );
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (!selectedEnv) {
      setError(new Error(t('errors.noEnvironments')));
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);
    setAccessDenied(false);
    setMetrics(null);
    setHistory(null);

    Promise.allSettled([
      doraApi.getMetrics(
        projectSlug,
        selectedEnv,
        days,
        annotationTargets ?? undefined,
      ),
      doraApi.getHistory(projectSlug, selectedEnv, days),
    ]).then(([metricsResult, historyResult]) => {
      if (cancelled) {
        return;
      }

      const accessDeniedResult = [metricsResult, historyResult].find(
        result =>
          result.status === 'rejected' &&
          isGitflicAccessDeniedError(result.reason),
      );

      if (accessDeniedResult) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (metricsResult.status === 'rejected') {
        setError(metricsResult.reason);
        setLoading(false);
        return;
      }

      setMetrics(metricsResult.value);
      if (historyResult.status === 'fulfilled') {
        setHistory(historyResult.value);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // selectedEnv?.name is intentional — avoids re-render loops from object identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doraApi, projectSlug, selectedEnv?.name, days, annotationTargets]);

  if (loading) return <Progress />;

  if (accessDenied) {
    return (
      <BuiAlert
        status="warning"
        icon
        title={t('errors.accessDenied.title')}
        description={t('errors.accessDenied.description')}
      />
    );
  }

  if (error) {
    return (
      <InfoCard title={t('title')}>
        <div style={{ padding: 24, color: doraTheme.metric.failure }}>
          <span style={{ fontWeight: 600 }}>{t('errors.label')} </span>
          {error.message}
        </div>
      </InfoCard>
    );
  }

  if (!metrics) return null;

  return (
    <InfoCard>
      <div style={{ padding: '4px 0', position: 'relative' }}>
        {/* Detailed overlay */}
        {expanded && (
          <DetailedOverlay
            data={expanded}
            days={days}
            entityWebBaseUrl={entityWebBaseUrl}
            onClose={() => setExpanded(null)}
          />
        )}

        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: textPrimary,
              letterSpacing: '-0.02em',
            }}
          >
            {t('title')}
          </div>
          {selectedEnv && (
            <div style={{ fontSize: 12, color: textSecondary, marginTop: 5 }}>
              {t('header.showingMetricsFor')}{' '}
              <strong style={{ color: textPrimary }}>{selectedEnv.name}</strong>{' '}
              —{' '}
              <span style={{ fontFamily: 'monospace' }}>
                {selectedEnv.branch}
              </span>
            </div>
          )}
        </div>

        {/* Filters row — left aligned */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 20,
          }}
        >
          {environments.length > 1 && (
            <div>
              <FilterLabel>{t('filters.environment')}</FilterLabel>
              <Select
                value={selectedEnvName}
                onValueChange={setSelectedEnvName}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('filters.selectEnvironment')} />
                </SelectTrigger>
                <SelectContent>
                  {environments.map(env => (
                    <SelectItem key={env.name} value={env.name}>
                      {env.name} ({env.branch})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <FilterLabel>{t('filters.dateRange')}</FilterLabel>
            <Select value={selectedDays} onValueChange={setSelectedDays}>
              <SelectTrigger>
                <SelectValue placeholder={t('filters.selectDateRange')} />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t('dates.lastDays', { days: opt.value })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Metric cards — sparklines inline, click to expand */}
        {(() => {
          const wkLabels = formatHistoryLabels(
            history ?? [],
            t('dates.locale'),
          );
          const deployData = history?.map(h => h.deploymentCount) ?? [];
          const leadData = history?.map(h => h.leadTimeHours) ?? [];
          const cfrData = history?.map(h => h.changeFailureRate ?? 0) ?? [];
          const mttrData = history?.map(h => h.mttrHours ?? 0) ?? [];
          const deploymentFrequencyTitle = t(
            'metric.deploymentFrequency.title',
          );
          const deploymentFrequencyDescription = t(
            'metric.deploymentFrequency.description',
          );
          const deploymentFrequencyTooltip = t(
            'metric.deploymentFrequency.tooltip',
          );
          const leadTimeTitle = t('metric.leadTimeForChanges.title');
          const leadTimeDescription = t(
            'metric.leadTimeForChanges.description',
          );
          const leadTimeTooltip = t('metric.leadTimeForChanges.tooltip');
          const changeFailureRateTitle = t('metric.changeFailureRate.title');
          const changeFailureRateDescription = t(
            'metric.changeFailureRate.description',
          );
          const changeFailureRateTooltip = t(
            'metric.changeFailureRate.tooltip',
          );
          const meanTimeToRestoreTitle = t('metric.meanTimeToRestore.title');
          const meanTimeToRestoreDescription = t(
            'metric.meanTimeToRestore.description',
          );
          const meanTimeToRestoreTooltip = t(
            'metric.meanTimeToRestore.tooltip',
          );
          const hotfixesToProductionTitle = t(
            'metric.hotfixesToProduction.title',
          );
          const hotfixLabel =
            selectedEnv?.label ?? t('metric.hotfixesToProduction.defaultLabel');
          const hotfixesToProductionDescription = t(
            'metric.hotfixesToProduction.description',
            { label: hotfixLabel },
          );
          const hotfixesToProductionTooltip = t(
            'metric.hotfixesToProduction.tooltip',
          );
          const productionOnlyDescription = t('metric.productionOnly');

          const bmMs = history?.map(h => h.bucketMidMs) ?? [];

          const mkExpand =
            (
              title: string,
              metric: DoraMetricValue,
              description: string,
              lowerIsBetter: boolean,
              sparklineValues: number[],
              sparklineType: 'bar' | 'line',
              sparklineColor: string,
              prs?: PrDetail[],
              prListLabel?: string,
              tickUnit?: 'hours' | 'count',
            ) =>
            () =>
              setExpanded({
                title,
                metric,
                description,
                lowerIsBetter,
                sparklineValues,
                sparklineType,
                sparklineColor,
                tickUnit,
                weekLabels: wkLabels,
                bucketMidMs: bmMs,
                prs,
                prListLabel,
              });

          // Per-bucket hotfix counts derived from hotfix MRs + bucket midpoints.
          const hotfixSparkData: number[] = bmMs.map(() => 0);
          if (bmMs.length > 0 && metrics?.numberOfHotfixes?.slowestPRs) {
            for (const pr of metrics.numberOfHotfixes.slowestPRs) {
              const time = new Date(pr.mergedAt).getTime();
              let bestIdx = 0,
                bestDist = Infinity;
              bmMs.forEach((mid, i) => {
                const d = Math.abs(time - mid);
                if (d < bestDist) {
                  bestDist = d;
                  bestIdx = i;
                }
              });
              hotfixSparkData[bestIdx]++;
            }
          }

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <MetricCard
                title={deploymentFrequencyTitle}
                tooltip={deploymentFrequencyTooltip}
                metric={metrics.deploymentFrequency}
                description={deploymentFrequencyDescription}
                sparklineData={deployData}
                sparklineType="bar"
                sparklineColor={doraTheme.metric.deployment}
                weekLabels={wkLabels}
                onExpand={mkExpand(
                  deploymentFrequencyTitle,
                  metrics.deploymentFrequency,
                  deploymentFrequencyDescription,
                  false,
                  deployData,
                  'bar',
                  doraTheme.metric.deployment,
                  metrics.deploymentFrequency.slowestPRs,
                  undefined,
                  'count',
                )}
              />
              <MetricCard
                title={leadTimeTitle}
                tooltip={leadTimeTooltip}
                metric={metrics.leadTime}
                description={leadTimeDescription}
                lowerIsBetter
                sparklineData={leadData}
                sparklineType="line"
                sparklineColor={doraTheme.metric.leadTime}
                weekLabels={wkLabels}
                onExpand={mkExpand(
                  leadTimeTitle,
                  metrics.leadTime,
                  leadTimeDescription,
                  true,
                  leadData,
                  'line',
                  doraTheme.metric.leadTime,
                  metrics.leadTime.slowestPRs,
                  undefined,
                  'hours',
                )}
              />
              {metrics.changeFailureRate !== null ? (
                <MetricCard
                  title={changeFailureRateTitle}
                  tooltip={changeFailureRateTooltip}
                  metric={metrics.changeFailureRate}
                  description={changeFailureRateDescription}
                  lowerIsBetter
                  sparklineData={cfrData}
                  sparklineType="line"
                  sparklineColor={doraTheme.metric.failure}
                  weekLabels={wkLabels}
                  onExpand={mkExpand(
                    changeFailureRateTitle,
                    metrics.changeFailureRate,
                    changeFailureRateDescription,
                    true,
                    cfrData,
                    'line',
                    doraTheme.metric.failure,
                    undefined,
                    undefined,
                    'count',
                  )}
                />
              ) : (
                <NaCard
                  title={changeFailureRateTitle}
                  tooltip={changeFailureRateTooltip}
                  description={productionOnlyDescription}
                />
              )}
              {metrics.mttr !== null ? (
                <MetricCard
                  title={meanTimeToRestoreTitle}
                  tooltip={meanTimeToRestoreTooltip}
                  metric={metrics.mttr}
                  description={meanTimeToRestoreDescription}
                  lowerIsBetter
                  sparklineData={mttrData}
                  sparklineType="line"
                  sparklineColor={doraTheme.metric.restore}
                  weekLabels={wkLabels}
                  onExpand={mkExpand(
                    meanTimeToRestoreTitle,
                    metrics.mttr,
                    meanTimeToRestoreDescription,
                    true,
                    mttrData,
                    'line',
                    doraTheme.metric.restore,
                    metrics.mttr.slowestPRs,
                    undefined,
                    'hours',
                  )}
                />
              ) : (
                <NaCard
                  title={meanTimeToRestoreTitle}
                  tooltip={meanTimeToRestoreTooltip}
                  description={productionOnlyDescription}
                />
              )}
              {metrics.numberOfHotfixes !== null ? (
                <MetricCard
                  title={hotfixesToProductionTitle}
                  tooltip={hotfixesToProductionTooltip}
                  metric={metrics.numberOfHotfixes}
                  description={hotfixesToProductionDescription}
                  lowerIsBetter
                  expandLabel={
                    metrics.numberOfHotfixes.value > 0
                      ? t('metric.hotfixesToProduction.expandLabel', {
                          value: String(metrics.numberOfHotfixes.value),
                          plural:
                            metrics.numberOfHotfixes.value !== 1 ? 's' : '',
                        })
                      : undefined
                  }
                  onExpand={mkExpand(
                    hotfixesToProductionTitle,
                    metrics.numberOfHotfixes,
                    hotfixesToProductionDescription,
                    true,
                    hotfixSparkData,
                    'bar',
                    doraTheme.metric.failure,
                    metrics.numberOfHotfixes.slowestPRs,
                    t('details.allHotfixes'),
                    'count',
                  )}
                />
              ) : (
                <NaCard
                  title={hotfixesToProductionTitle}
                  tooltip={hotfixesToProductionTooltip}
                  description={productionOnlyDescription}
                />
              )}
            </div>
          );
        })()}
      </div>
    </InfoCard>
  );
}
